import React from 'react';

import styles from './TaskDateAndUrgency.module.scss';

import { DateFormat, DateHelper } from '@utils/DateHelper';
import { TimezoneHelper } from '@utils/TimezoneHelper';

import { TaskSection } from '../TaskSection';
import { TaskInputSubstitutor } from '../TaskInputSubstitutor';
import { TaskAttributes } from 'chaika-test-attributes';
import { translate } from '@chaika/i18n/helper';
import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';
import { Checkbox } from '@components/Checkbox';
import { DatePicker } from '@components/datePickers';
import { TaskUrgency } from 'chaika-enums';

export function TaskDateAndUrgency(): React.ReactElement<any> {
    const { state, permissions, actions } = useTaskForm();
    const { isEditMode, urgency, start = new Date(), end = new Date(), timezone } = state;
    const canEdit = isEditMode && permissions.canEditDate;

    const isUrgent = urgency === TaskUrgency.Urgent;
    const startDay = TimezoneHelper.toTimeZoneFromLocalTimezone(start, timezone);
    const endDay = TimezoneHelper.toTimeZoneFromLocalTimezone(end, timezone);

    const onChangeStart = (date: Nullable<Date>): void => {
        actions.setStartDate(date);
    };

    const onChangeEnd = (date: Nullable<Date>): void => {
        actions.setEndDate(date);
    };

    const onChangeUrgency = (event: React.ChangeEvent<HTMLInputElement>): void => {
        actions.setUrgency(event.target.checked ? TaskUrgency.Urgent : TaskUrgency.Routine);
    };

    return (
        <TaskSection
            title={translate('dateTimeFrame')}
            rowGap={20}
        >
            <div className={styles.completeDate}>
                <TaskInputSubstitutor
                    rowDirection
                    canEdit={canEdit}
                    label={[translate('doFrom'), translate('doTo')]}
                    value={[
                        DateHelper.formatIfNotNil(startDay, DateFormat.DateWithDotSeparator),
                        DateHelper.formatIfNotNil(endDay, DateFormat.DateWithDotSeparator),
                    ]}
                    dataTest={[TaskAttributes.Date, TaskAttributes.EndDate]}
                >
                    <>
                        <div className={styles.dates}>
                            <DatePicker
                                alwaysShowMask
                                title={translate('doFrom')}
                                customMask={'00.00.0000'}
                                name={'start'}
                                disabled={isUrgent}
                                selected={startDay}
                                onChange={onChangeStart}
                                dataTest={TaskAttributes.Date}
                            />
                            <DatePicker
                                alwaysShowMask
                                title={translate('doTo')}
                                customMask={'00.00.0000'}
                                name={'end'}
                                disabled={isUrgent}
                                minDate={startDay}
                                selected={endDay}
                                onChange={onChangeEnd}
                                dataTest={TaskAttributes.EndDate}
                            />
                        </div>
                        <div className={styles.urgency}>
                            <Checkbox
                                label={translate('urgent')}
                                isChecked={isUrgent}
                                onChange={onChangeUrgency}
                            />
                        </div>
                    </>
                </TaskInputSubstitutor>
            </div>
        </TaskSection>
    );
}
